import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { LogoFull } from '../components/Logo';

const API_BASE = process.env.REACT_APP_API_URL || 'https://football-predictions-fawn.vercel.app';

// Schedule slot presets (CET times as UTC)
const SCHEDULE_PRESETS = [
  { label: 'Morning (8 AM CET)', hour: 7, minute: 0 },
  { label: 'Midday (12:30 PM CET)', hour: 11, minute: 30 },
  { label: 'Evening (6:30 PM CET)', hour: 17, minute: 30 },
];

function AdminContent() {
  const { user, session } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [editingId, setEditingId] = useState(null);
  const [editCaption, setEditCaption] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const res = await fetch(
        `${API_BASE}/api/social/pending-content?status=${filter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.status === 403) {
        setError('Admin access required');
        setItems([]);
        return;
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [session, filter]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const updateItem = async (id, updates) => {
    try {
      const token = session?.access_token;
      const res = await fetch(`${API_BASE}/api/social/pending-content`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id, ...updates }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchItems();
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this content item?')) return;

    try {
      const token = session?.access_token;
      const res = await fetch(`${API_BASE}/api/social/pending-content?id=${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchItems();
    } catch (err) {
      setError(err.message);
    }
  };

  const approveWithSchedule = async (id, scheduledFor) => {
    await updateItem(id, { status: 'approved', scheduled_for: scheduledFor });
  };

  const startEditing = (item) => {
    setEditingId(item.id);
    setEditCaption(item.caption);
  };

  const saveEdit = async (id) => {
    await updateItem(id, { caption: editCaption });
    setEditingId(null);
    setEditCaption('');
  };

  return (
    <div className="min-h-screen bg-anthracite-950">
      {/* Header */}
      <div className="border-b border-anthracite-700 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <LogoFull subtitle />
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">Admin</span>
            <a href="/dashboard" className="text-sm text-accent hover:text-accent-light">
              Dashboard
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-bold text-white mb-2">Content Queue</h1>
        <p className="text-gray-400 mb-8">Review and schedule social media content</p>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6">
          {['pending', 'approved', 'posted', 'rejected', 'failed'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === status
                  ? 'bg-accent text-white'
                  : 'bg-anthracite-800 text-gray-400 hover:text-white'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 rounded-lg bg-loss/10 border border-loss/30 text-loss">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Empty state */}
        {!loading && items.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No {filter} content items.
          </div>
        )}

        {/* Content cards */}
        <div className="space-y-4">
          {items.map(item => (
            <ContentCard
              key={item.id}
              item={item}
              isEditing={editingId === item.id}
              editCaption={editCaption}
              onEditCaption={setEditCaption}
              onStartEditing={() => startEditing(item)}
              onSaveEdit={() => saveEdit(item.id)}
              onCancelEdit={() => { setEditingId(null); setEditCaption(''); }}
              onApprove={(scheduledFor) => approveWithSchedule(item.id, scheduledFor)}
              onReject={() => updateItem(item.id, { status: 'rejected' })}
              onDelete={() => deleteItem(item.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ContentCard({
  item,
  isEditing,
  editCaption,
  onEditCaption,
  onStartEditing,
  onSaveEdit,
  onCancelEdit,
  onApprove,
  onReject,
  onDelete,
}) {
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('07:00');

  const platformIcon = item.platform === 'instagram' ? 'IG' : 'TW';
  const platformColor = item.platform === 'instagram' ? 'text-pink-400' : 'text-blue-400';

  const typeLabels = {
    top_picks: 'Top Picks',
    carousel: 'Carousel',
    preview_thread: 'Preview Thread',
    weekly_dashboard: 'Weekly Dashboard',
  };

  // Pre-fill schedule date with tomorrow
  useEffect(() => {
    if (!scheduleDate) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setScheduleDate(tomorrow.toISOString().split('T')[0]);
    }
  }, [scheduleDate]);

  const handleApprovePreset = (preset) => {
    const date = scheduleDate || new Date().toISOString().split('T')[0];
    const scheduled = new Date(`${date}T${String(preset.hour).padStart(2, '0')}:${String(preset.minute).padStart(2, '0')}:00Z`);
    onApprove(scheduled.toISOString());
  };

  const handleApproveCustom = () => {
    const scheduled = new Date(`${scheduleDate}T${scheduleTime}:00Z`);
    onApprove(scheduled.toISOString());
  };

  return (
    <div className="rounded-xl bg-anthracite-800 border border-anthracite-700 overflow-hidden">
      {/* Header row */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-anthracite-700">
        <div className="flex items-center gap-3">
          <span className={`text-xs font-bold ${platformColor} bg-anthracite-700 px-2 py-1 rounded`}>
            {platformIcon}
          </span>
          <span className="text-sm font-medium text-white">
            {typeLabels[item.content_type] || item.content_type}
          </span>
          <StatusBadge status={item.status} />
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          {item.scheduled_for && (
            <span>
              Scheduled: {new Date(item.scheduled_for).toLocaleString()}
            </span>
          )}
          <span>{new Date(item.created_at).toLocaleDateString()}</span>
        </div>
      </div>

      {/* Body */}
      <div className="p-5">
        {/* Image preview */}
        {item.image_urls && item.image_urls.length > 0 && (
          <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
            {item.image_urls.map((url, i) => (
              <img
                key={i}
                src={url}
                alt={`Content ${i + 1}`}
                className="h-40 rounded-lg border border-anthracite-600 flex-shrink-0"
              />
            ))}
          </div>
        )}

        {/* Caption */}
        <div className="mb-4">
          <label className="text-xs text-gray-500 uppercase tracking-wider mb-1 block">Caption</label>
          {isEditing ? (
            <div className="space-y-2">
              <textarea
                value={editCaption}
                onChange={(e) => onEditCaption(e.target.value)}
                className="w-full bg-anthracite-900 border border-anthracite-600 rounded-lg p-3 text-white text-sm resize-y min-h-[100px]"
              />
              <div className="flex gap-2">
                <button
                  onClick={onSaveEdit}
                  className="px-3 py-1.5 bg-accent text-white text-sm rounded-lg hover:bg-accent-dark"
                >
                  Save
                </button>
                <button
                  onClick={onCancelEdit}
                  className="px-3 py-1.5 bg-anthracite-700 text-gray-400 text-sm rounded-lg hover:text-white"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p
              className="text-sm text-gray-300 whitespace-pre-wrap cursor-pointer hover:text-white"
              onClick={onStartEditing}
              title="Click to edit"
            >
              {item.caption}
            </p>
          )}
        </div>

        {/* Hashtags */}
        {item.hashtags && item.hashtags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {item.hashtags.map((tag, i) => (
              <span key={i} className="text-xs text-accent bg-accent/10 px-2 py-0.5 rounded">
                #{tag.replace(/^#/, '')}
              </span>
            ))}
          </div>
        )}

        {/* Thread preview */}
        {item.metadata?.thread && (
          <div className="mb-4">
            <label className="text-xs text-gray-500 uppercase tracking-wider mb-1 block">Thread</label>
            <div className="space-y-2">
              {item.metadata.thread.map((tweet, i) => (
                <div key={i} className="text-sm text-gray-400 bg-anthracite-900 rounded-lg p-3 border-l-2 border-blue-400">
                  {tweet}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions — only for pending items */}
        {item.status === 'pending' && (
          <div className="border-t border-anthracite-700 pt-4 mt-4">
            <div className="flex flex-wrap items-end gap-4">
              {/* Quick schedule buttons */}
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider mb-2 block">
                  Approve & Schedule
                </label>
                <div className="flex gap-2">
                  {SCHEDULE_PRESETS.map((preset) => (
                    <button
                      key={preset.label}
                      onClick={() => handleApprovePreset(preset)}
                      className="px-3 py-1.5 bg-win/10 border border-win/30 text-win text-xs rounded-lg hover:bg-win/20"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom schedule */}
              <div className="flex items-end gap-2">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Date</label>
                  <input
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    className="bg-anthracite-900 border border-anthracite-600 rounded-lg px-3 py-1.5 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Time (UTC)</label>
                  <input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    className="bg-anthracite-900 border border-anthracite-600 rounded-lg px-3 py-1.5 text-white text-sm"
                  />
                </div>
                <button
                  onClick={handleApproveCustom}
                  className="px-3 py-1.5 bg-win text-white text-sm rounded-lg hover:bg-win/80"
                >
                  Approve
                </button>
              </div>

              {/* Reject / Delete */}
              <div className="flex gap-2 ml-auto">
                <button
                  onClick={onReject}
                  className="px-3 py-1.5 bg-loss/10 border border-loss/30 text-loss text-xs rounded-lg hover:bg-loss/20"
                >
                  Reject
                </button>
                <button
                  onClick={onDelete}
                  className="px-3 py-1.5 bg-anthracite-700 text-gray-500 text-xs rounded-lg hover:text-loss"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Posted info */}
        {item.status === 'posted' && item.platform_post_id && (
          <div className="text-xs text-gray-500 mt-2">
            Post ID: {item.platform_post_id} | Posted: {new Date(item.posted_at).toLocaleString()}
          </div>
        )}

        {/* Error info */}
        {item.status === 'failed' && item.posting_error && (
          <div className="mt-2 p-3 rounded-lg bg-loss/10 border border-loss/30 text-loss text-sm">
            Error: {item.posting_error}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    approved: 'bg-blue-500/20 text-blue-400',
    posted: 'bg-win/20 text-win',
    rejected: 'bg-anthracite-600 text-gray-400',
    failed: 'bg-loss/20 text-loss',
  };

  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded ${styles[status] || styles.pending}`}>
      {status}
    </span>
  );
}

export default AdminContent;
